import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useBusinessUnitMutations } from '@/hooks/use-business-units';
import type { BusinessUnit } from '@/types';

// Mirrors machine's slugify (backend/services/businessUnits.service.js), both to
// prefill the field from the name and to normalise a hand-typed slug on submit.
// Validating a laxer shape instead would accept '-acme--eu' here and store
// 'acme-eu' there, with nothing telling the operator it had been rewritten.
const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  slug: z.string().max(64).transform(slugify).optional()
});

type FormData = z.infer<typeof schema>;

interface Props {
  /** Omit to create; pass a BU to rename it. */
  businessUnit?: BusinessUnit;
  trigger: React.ReactNode;
}

export function BusinessUnitDialog({ businessUnit, trigger }: Props) {
  const isEdit = Boolean(businessUnit);
  const { createBusinessUnit, updateBusinessUnit } = useBusinessUnitMutations();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: businessUnit?.name ?? '',
      slug: businessUnit?.slug ?? ''
    }
  });

  const name = watch('name');

  // Keep slug in step with the name until the operator edits the slug directly.
  useEffect(() => {
    if (!slugTouched && !isEdit) setValue('slug', slugify(name ?? ''));
  }, [name, slugTouched, isEdit, setValue]);

  useEffect(() => {
    if (open) {
      reset({ name: businessUnit?.name ?? '', slug: businessUnit?.slug ?? '' });
      setSlugTouched(false);
    }
  }, [open, businessUnit, reset]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        ...(data.slug ? { slug: data.slug } : {})
      };
      if (isEdit && businessUnit) {
        await updateBusinessUnit(businessUnit.id, payload);
        toast.success('Business unit updated');
      } else {
        await createBusinessUnit(payload);
        toast.success('Business unit created');
      }
      setOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not save business unit'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit business unit' : 'Add a business unit'}
          </DialogTitle>
          <DialogDescription>
            A business unit is a brand inside this account. Its id is permanent;
            the name and slug can change later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='bu-name'>Name</Label>
            <Input id='bu-name' autoFocus {...register('name')} />
            {errors.name && (
              <p className='text-xs text-destructive'>{errors.name.message}</p>
            )}
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='bu-slug'>Slug</Label>
            <Input
              id='bu-slug'
              placeholder='auto-generated from name'
              {...register('slug', { onChange: () => setSlugTouched(true) })}
            />
            {errors.slug && (
              <p className='text-xs text-destructive'>{errors.slug.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type='submit' disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Add business unit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
